// Subscribe Mint confirmation widget — state-aware (Phase 6D).
//
// Variant lookup driven by dossier currentPhase + on-chain sale status:
//
//   currentPhase   refundsEnabled   saleFinalized   Variant
//   ─────────────  ───────────────  ──────────────  ────────────
//   06-purchased   true             *                refundable
//   06-purchased   false            true             claimable
//   06-purchased   false            false            escrowed
//   07-holding     *                *                holding
//   08-settled     *                *                settled
//   09-refunded    *                *                refunded
//   ≥ 10-*         *                *                historic (defensive)
//
// Read-only — no widget POST. Lifecycle advance to 07-holding is
// server-side via distribution.received. Claim CTA + refund CTA are
// Phase 9 (or "6E"); 6D is purely confirmation + escrow-status UX.
//
// Polling: when variant === 'escrowed', useTokenSaleStatus polls
// every 60s (cache TTL is 15s but sale finalization timescale is
// days, so 60s is fine). On `saleFinalized` flip → variant becomes
// `claimable` automatically.

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Money, getExplorerUrl } from '@rmint/ui';
import { useSubscriptionState } from '../../lib/use-subscription-state';
import { useOffering, type Offering } from '../../lib/offering';
import { useTokenSaleStatus } from '../../lib/onchain';
import { env } from '@rmint/shell';
import type { WidgetProps } from './registry';

const FALLBACK_OFFERING: Offering = {
  rmintId: 'sakura-ramen-test-d1',
  restaurantName: 'Sakura Ramen NC',
  ticker: 'RMOT-SAKURA-D1',
  tokenContractAddress: '0xB03e42d3B2F86343dEcC3631bBd792F7f988eCAd',
  saleContractAddress: '0xf8FF966494E91283Ca7b744c60F12811C6bD92dC',
  pricePerToken: 1.0,
  minAmountUsdc: 1000,
  maxAmountUsdc: 50000,
  lockupMonths: 36,
  lockupEndIso: '2029-05-09T00:00:00Z',
  signByDeadlineIso: '2026-06-09T00:00:00Z',
  estimatedFirstDistributionIso: '2026-08-15T00:00:00Z',
  networkFeeUsdcEst: 2.5,
};

export type Variant =
  | 'escrowed'
  | 'claimable'
  | 'refundable'
  | 'holding'
  | 'settled'
  | 'refunded'
  | 'historic';

const HOLDING_PHASES = new Set(['07-holding']);
const SETTLED_PHASES = new Set(['08-settled']);
const REFUNDED_PHASES = new Set(['09-refunded']);

// Exported for unit testing — pure function, no React.
export function selectMintVariant(
  currentPhase: string | undefined,
  saleFinalized: boolean | null,
  refundsEnabled: boolean | null,
): Variant {
  if (!currentPhase) return 'escrowed'; // defensive default
  if (REFUNDED_PHASES.has(currentPhase)) return 'refunded';
  if (SETTLED_PHASES.has(currentPhase)) return 'settled';
  if (HOLDING_PHASES.has(currentPhase)) return 'holding';
  if (currentPhase === '06-purchased') {
    if (refundsEnabled === true) return 'refundable';
    if (saleFinalized === true) return 'claimable';
    return 'escrowed';
  }
  // Phases >= 10-* (kyc-rejected etc.) — defensive
  return 'historic';
}

// Window.ethereum is globally typed by viem (EIP1193Provider) but
// wallet_watchAsset isn't in the strict EIP-1193 method union.
// Cast to a permissive shape only at the call site.
type EthRequest = (args: { method: string; params?: unknown }) => Promise<unknown>;

type PurchaseData = {
  txHash?: string;
  blockNumber?: number;
  amountUsdc?: number;
  rotAmount?: number;
  chainId?: number;
  investorId?: string;
  rmintId?: string;
};

export function SubscribeMintConfirmation(props: WidgetProps) {
  const navigate = useNavigate();
  const subscriptionId =
    (props.subscriptionId as string | undefined) ??
    (props.subscriptionid as string | undefined) ??
    '';

  const debugStateOverride = props.state as string | undefined;

  const sub = useSubscriptionState(subscriptionId, 0);

  const dossierRmintId = (sub.state?.raw.data as PurchaseData | undefined)?.rmintId;
  const propRmintId = props.rmintId as string | undefined;
  const rmintId = dossierRmintId ?? propRmintId;
  const off = useOffering(rmintId);
  const offering: Offering = off.offering ?? FALLBACK_OFFERING;

  const saleStatus = useTokenSaleStatus(offering.saleContractAddress);
  const saleFinalized = saleStatus.data?.finalized ?? null;
  const refundsEnabled = saleStatus.data?.refundsEnabled ?? null;

  const dossierVariant = selectMintVariant(
    sub.state?.currentPhase,
    saleFinalized,
    refundsEnabled,
  );
  const overrideVariant: Variant | null =
    debugStateOverride === 'escrowed' ||
    debugStateOverride === 'claimable' ||
    debugStateOverride === 'refundable' ||
    debugStateOverride === 'holding' ||
    debugStateOverride === 'settled' ||
    debugStateOverride === 'refunded' ||
    debugStateOverride === 'historic'
      ? (debugStateOverride as Variant)
      : null;
  // Legacy debug overrides "just" -> escrowed for backward compat with prior skeleton.
  const compatOverride: Variant | null =
    debugStateOverride === 'just' ? 'escrowed' : null;
  const variant: Variant = overrideVariant ?? compatOverride ?? dossierVariant;

  // 60s polling while escrowed — auto-flips to claimable when sale
  // finalizes. Cache TTL is 15s; we poll less often because
  // finalization timescale is days, not seconds.
  useEffect(() => {
    if (variant !== 'escrowed') return;
    const refresh = (saleStatus as { refresh?: () => void }).refresh;
    if (!refresh) return;
    const id = setInterval(() => refresh(), 60_000);
    return () => clearInterval(id);
  }, [variant, saleStatus.refresh]);

  // Read purchase data from state.data (at 06-purchased)
  // NOTE: at 07-holding+, state.data carries distribution.received's
  // data instead of subscription.purchased's. The lifecycle/06-purchased
  // fallback is deferred to Phase 9 since 07+ requires real revenue.
  const subData = sub.state?.raw.data as PurchaseData | undefined;
  const txHash = subData?.txHash;
  const amountUsdc = subData?.amountUsdc ?? 0;
  const rotAmount = subData?.rotAmount ?? Math.floor(amountUsdc / offering.pricePerToken);

  async function addToWallet() {
    const request = (window as { ethereum?: { request?: EthRequest } }).ethereum?.request;
    if (!request) return;
    try {
      await request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: offering.tokenContractAddress,
            symbol: offering.ticker,
            decimals: 18,
          },
        },
      });
    } catch {
      // user rejected — no-op
    }
  }

  // --- Render branches ---

  if (variant === 'refunded') {
    return (
      <div className="rounded-card border border-border bg-card p-4 space-y-1 text-[13px]">
        <p className="font-semibold text-ink">Refunded</p>
        <p className="text-[11px] text-foreground-mute">
          Sale was cancelled and your USDC was returned.
        </p>
      </div>
    );
  }

  if (variant === 'settled') {
    return (
      <div className="rounded-card border border-border bg-card p-4 space-y-1 text-[13px]">
        <p className="font-semibold text-ink">Position settled</p>
        <p className="text-[11px] text-foreground-mute">
          {rotAmount.toLocaleString()} {offering.ticker} · subscription complete.
        </p>
      </div>
    );
  }

  if (variant === 'historic') {
    return (
      <div className="rounded-card border border-border bg-card p-4 space-y-1 text-[13px]">
        <p className="font-semibold text-ink">Subscription past mint step</p>
        <p className="text-[11px] text-foreground-mute">
          Phase: {sub.state?.currentPhase ?? 'unknown'}.
        </p>
      </div>
    );
  }

  if (variant === 'holding') {
    return (
      <div className="rounded-card border border-border bg-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-border-soft px-6 py-4">
          <h3 className="text-base font-bold text-ink">Holding summary</h3>
          <span className="font-mono text-[11px] text-foreground-mute">07-holding</span>
        </div>
        <div className="px-6 py-4 grid grid-cols-[1fr_auto] gap-y-2 text-[13px]">
          <span className="text-foreground-mute">Tokens</span>
          <span className="font-mono text-right font-bold">{rotAmount.toLocaleString()} {offering.ticker}</span>
          <span className="text-foreground-mute">Funded</span>
          <span className="font-mono text-right"><Money value={amountUsdc} unit="USDC" /></span>
          <span className="text-foreground-mute">Distributions to date</span>
          <span className="font-mono text-right"><Money value={0} /></span>
          <span className="text-foreground-mute">Next distribution (est.)</span>
          <span className="text-right">~Q3 2026</span>
        </div>
        <div className="px-6 py-3 border-t border-border-soft flex gap-2">
          {txHash ? (
            <a href={getExplorerUrl(env.chainEnv, 'tx', txHash) ?? '#'} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-input border border-border text-[12px] font-semibold text-foreground hover:bg-muted">Mint tx ↗</a>
          ) : null}
          <a href={getExplorerUrl(env.chainEnv, 'address', offering.tokenContractAddress) ?? '#'} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-input border border-border text-[12px] font-semibold text-foreground hover:bg-muted">Token contract ↗</a>
          <button onClick={addToWallet} className="px-3 py-2 rounded-input border border-border text-[12px] font-semibold text-foreground hover:bg-muted">+ Add to wallet</button>
        </div>
      </div>
    );
  }

  // --- 06-purchased variants: escrowed / claimable / refundable ---
  // All share the celebratory header + transaction summary; differ in
  // the banner copy below.

  const banner =
    variant === 'refundable' ? (
      <div className="rounded-card border border-warn-line bg-warn-bg px-4 py-3 text-[13px] text-warn">
        <strong>Sale cancelled.</strong> Refund flow lands in Phase 9. Contact support for an out-of-band refund in the meantime.
      </div>
    ) : variant === 'claimable' ? (
      <div className="rounded-card border border-success-line bg-success-bg px-4 py-3 text-[13px]">
        <strong>Sale finalized.</strong> Tokens are ready to claim. Claim flow lands in Phase 9.
      </div>
    ) : (
      <div className="rounded-card border border-info-line bg-info-bg px-4 py-3 text-[13px]">
        <strong>Purchase complete.</strong> Tokens are escrowed in the Sale contract. They claim to your custody Safe after the sale finalizes (admin action; ~end of sale period).
      </div>
    );

  return (
    <div className="rounded-card border border-border bg-card p-8 text-center space-y-5">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success-bg border-[3px] border-success text-success text-3xl font-bold mx-auto">
        ✓
      </div>
      <div>
        <h2 className="text-2xl font-bold text-ink">Welcome to {offering.restaurantName}</h2>
        <p className="text-[14px] text-foreground-mute mt-1">Your subscription is recorded on-chain.</p>
      </div>
      <div className="rounded-card border border-border bg-card-warm p-5 max-w-md mx-auto text-left grid grid-cols-[1fr_auto] gap-y-2 text-[13px]">
        <span className="text-foreground-mute">Tokens purchased</span>
        <span className="font-mono text-right font-bold">{rotAmount.toLocaleString()} {offering.ticker}</span>
        <span className="text-foreground-mute">Funded</span>
        <span className="font-mono text-right"><Money value={amountUsdc} unit="USDC" /></span>
        {txHash ? (
          <>
            <span className="text-foreground-mute">Purchase transaction</span>
            <span className="font-mono text-right">
              <a href={getExplorerUrl(env.chainEnv, 'tx', txHash) ?? '#'} target="_blank" rel="noreferrer" className="text-primary no-underline hover:underline">
                {txHash.slice(0, 6)}...{txHash.slice(-4)} ↗
              </a>
            </span>
          </>
        ) : null}
        <span className="text-foreground-mute">Token contract</span>
        <span className="font-mono text-right">
          <a href={getExplorerUrl(env.chainEnv, 'address', offering.tokenContractAddress) ?? '#'} target="_blank" rel="noreferrer" className="text-primary no-underline hover:underline">
            {offering.tokenContractAddress.slice(0, 6)}...{offering.tokenContractAddress.slice(-4)} ↗
          </a>
        </span>
      </div>
      <div className="max-w-md mx-auto">{banner}</div>
      <div className="flex justify-center gap-3">
        <button onClick={addToWallet} className="px-4 py-2 rounded-input border border-border text-[13px] font-semibold text-foreground hover:bg-muted">+ Add {offering.ticker} to wallet</button>
        {txHash ? (
          <a href={getExplorerUrl(env.chainEnv, 'tx', txHash) ?? '#'} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-input border border-border text-[13px] font-semibold text-foreground hover:bg-muted">View on Basescan ↗</a>
        ) : null}
      </div>
      <div className="pt-3">
        <Button variant="primary" size="lg" onClick={() => navigate('/')}>
          Back to portfolio →
        </Button>
      </div>
    </div>
  );
}
